/**
 * Shows a form read-only until the user starts editing, via the edit button or by clicking a field.
 * Save stays disabled until a value changed; cancel (or Escape) restores the stored values.
 */
export function attachInlineEdit(form: HTMLFormElement): void {
    const inputs = Array.from(form.querySelectorAll<HTMLInputElement>("input:not([type=hidden])"));
    const editButton = form.querySelector<HTMLElement>("[data-inline-edit-start]");
    const actions = form.querySelector<HTMLElement>("[data-inline-edit-actions]");
    const cancelButton = form.querySelector<HTMLElement>("[data-inline-edit-cancel]");
    const submitButton = form.querySelector<HTMLButtonElement>("button[type=submit]");

    const isDirty = (): boolean => {
        return inputs.some(x => x.value !== x.defaultValue);
    };

    const setEditing = (editing: boolean): void => {
        inputs.forEach(x => {
            x.readOnly = !editing;
        });

        editButton?.classList.toggle("mtx-hidden", editing);
        actions?.classList.toggle("mtx-hidden", !editing);

        // A disabled submit button also blocks implicit submission (Enter) while read-only
        if (submitButton) {
            submitButton.disabled = !editing || !isDirty();
        }
    };

    const startEditing = (focusInput?: HTMLInputElement): void => {
        setEditing(true);
        (focusInput ?? inputs[0])?.focus();
    };

    const cancelEditing = (): void => {
        // Restores the default values, i.e. the values rendered by the server
        form.reset();
        form.classList.remove("was-validated");
        setEditing(false);
        editButton?.focus();
    };

    editButton?.addEventListener("click", () => startEditing());
    cancelButton?.addEventListener("click", () => cancelEditing());

    inputs.forEach(x => {
        x.addEventListener("click", () => {
            if (x.readOnly) {
                startEditing(x);
            }
        });
        x.addEventListener("input", () => {
            if (submitButton) {
                submitButton.disabled = !isDirty();
            }
        });
    });

    form.addEventListener("keydown", event => {
        if (event.key === "Escape") {
            cancelEditing();
        }
    });

    // After a server-side validation error the form must stay open, so the user sees and can fix the values
    const hasErrors = form.querySelector(".field-validation-error, .validation-summary-errors") !== null;
    setEditing(hasErrors);
    if (hasErrors && submitButton) {
        submitButton.disabled = false;
    }
}
